import { COMPANY_ROLES, companyKind, roleLabel } from '../pages/Admin/adminRoles';

export const NAV_ACCESS = {
  dashboard: 'dashboard',
  overview: 'overview',
  tools: 'tools',
  wizetok: 'wizetok',
  administration: 'administration',
  settings: 'settings',
};

const ROLE_NAV = {
  [COMPANY_ROLES.admin]: new Set(['dashboard', 'overview', 'administration']),
  [COMPANY_ROLES.owner]: new Set(['dashboard', 'overview', 'administration']),
  [COMPANY_ROLES.manager]: new Set([
    'dashboard', 'overview', 'tools', 'wizetok', 'administration', 'settings',
  ]),
  [COMPANY_ROLES.recruiter]: new Set([
    'dashboard', 'overview', 'tools', 'wizetok', 'administration', 'settings',
  ]),
  [COMPANY_ROLES.viewer]: new Set(['tools', 'wizetok']),
};

const TOOL_PREFIXES = [
  '/open-positions', '/jd-preview', '/database', '/stack-ranking',
  '/existing-positions', '/resume-formatter', '/engagement',
  '/whatsapp-reachout', '/candidates',
];

// A session's "kind" is one role kind, or an array of kinds when the user holds
// several roles (e.g. Admin + Manager). Access is the union of every kind held.
function kindsOf(kind) {
  return Array.isArray(kind) ? kind : [kind];
}

export function sessionCompanyKind(tenantRole, role, unrestricted, tenantRoles) {
  if (unrestricted) return 'platform';
  const held = Array.isArray(tenantRoles) && tenantRoles.length ? tenantRoles : [tenantRole || role];
  const kinds = [...new Set(held.filter(Boolean).map(companyKind))];
  if (kinds.includes(COMPANY_ROLES.admin) || kinds.includes(COMPANY_ROLES.owner)) {
    return kinds.length > 1 ? kinds : COMPANY_ROLES.admin;
  }
  if (kinds.length > 1) return kinds;
  return kinds[0] || companyKind(tenantRole || role);
}

export function profileRoleLabel(tenantRole, role, unrestricted, tenantRoles) {
  if (unrestricted) return 'Platform';
  const held = Array.isArray(tenantRoles) && tenantRoles.length ? tenantRoles : [tenantRole || role];
  const labels = [...new Set(held.filter(Boolean).map(roleLabel))];
  if (labels.length > 1) return labels.join(' + ');
  return labels[0] || roleLabel(tenantRole || role);
}

export function canAccessNav(kind, access) {
  if (!access) return true;
  return kindsOf(kind).some((k) => k === 'platform' || ROLE_NAV[k]?.has(access) === true);
}

export function canWrite(kind) {
  return kindsOf(kind).some((k) => k === 'platform' || k !== COMPANY_ROLES.viewer);
}

export function homePathForKind(kind) {
  const kinds = kindsOf(kind);
  if (kinds.includes(COMPANY_ROLES.admin) || kinds.includes(COMPANY_ROLES.owner)) return '/analytics';
  return '/open-positions';
}

export function homePathFromUser(user) {
  if (!user) return '/open-positions';
  const unrestricted = user.role === 'SUPER_ADMIN' && !user.tenantId && !user.tenantRole;
  return homePathForKind(sessionCompanyKind(user.tenantRole, user.role, unrestricted, user.tenantRoles));
}

export function pathAccessKeys(pathname) {
  const path = String(pathname || '');
  if (path === '/open-wizetok' || path.startsWith('/open-wizetok/') || path.startsWith('/sso/wizetok')) {
    return ['wizetok'];
  }
  if (path === '/admin' || path.startsWith('/admin/')) {
    return ['administration'];
  }
  if (path === '/analytics' || path.startsWith('/analytics/')) {
    return ['dashboard', 'overview'];
  }
  if (TOOL_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return ['tools'];
  }
  return [];
}

export function isPathAllowedForRole(pathname, kind) {
  if (kindsOf(kind).includes('platform')) return true;
  const keys = pathAccessKeys(pathname);
  if (keys.length === 0) return true;
  return keys.some((key) => canAccessNav(kind, key));
}

export function resolveLandingPath(user, lastPath) {
  const kind = sessionCompanyKind(
    user?.tenantRole,
    user?.role,
    user?.role === 'SUPER_ADMIN' && !user?.tenantId && !user?.tenantRole,
    user?.tenantRoles,
  );
  const path = String(lastPath || '').split('?')[0];
  if (path && isPathAllowedForRole(path, kind)) return lastPath;
  return homePathForKind(kind);
}

const WRITE_HINT = /\b(add|create|save|delete|remove|edit|update|upload|send|generate|import|new position|format resume|analyze|invite|publish|archive|shortlist)\b/i;

export function installViewerWriteGuard() {
  const onClick = (event) => {
    const el = event.target.closest('button, a[href], [role="button"], input[type="submit"], input[type="file"]');
    if (!el) return;
    if (el.closest('.app-sidebar, [data-role-nav]')) return;
    if (el.type === 'file') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const label = `${el.getAttribute('aria-label') || ''} ${el.textContent || ''} ${el.title || ''}`;
    if (WRITE_HINT.test(label) || el.closest('[data-write-action]')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.querySelector('input[type="search"]')) return;
    if (form.querySelector('input[type="file"], textarea, select') || WRITE_HINT.test(form.textContent || '')) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);
  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('submit', onSubmit, true);
  };
}
